<template>
  <div>
    <!-- First-visit consent bar (pixels spec §5a). Cannot be dismissed
         without an explicit choice: no close-X, no click-outside, no Escape.
         Accept All and Reject All render with visually equal prominence
         (counsel condition #3) — same variant, same width, side by side. -->
    <div
      v-if="showBanner"
      class="consent-banner"
      role="dialog"
      aria-label="Cookie consent"
    >
      <b-container>
        <b-row align-v="center">
          <b-col cols="12" lg="7" class="mb-2 mb-lg-0">
            <p class="mb-0 consent-banner__copy">
              We use cookies and similar technologies for analytics and
              marketing. You can accept all, reject all, or manage your
              preferences. See our
              <b-link href="/api/help/articles/cookie-and-privacy-policy"
                      target="_blank">cookie &amp; privacy policy</b-link>.
            </p>
          </b-col>
          <b-col cols="12" lg="5">
            <div class="consent-banner__actions">
              <b-button
                class="consent-banner__btn"
                variant="outline-primary"
                v-b-tooltip.hover
                title="Block all non-essential cookies, including issuer marketing pixels and Invown's own analytics."
                @click="choose('rejected')"
              >Reject all</b-button>
              <b-button
                class="consent-banner__btn"
                variant="outline-primary"
                v-b-tooltip.hover
                title="Allow Invown's own analytics and issuer marketing pixels to use cookies on this site."
                @click="choose('accepted')"
              >Accept all</b-button>
              <b-button
                class="consent-banner__btn"
                variant="link"
                v-b-tooltip.hover
                title="Choose which cookie categories to allow individually."
                @click="openManage"
              >Manage</b-button>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </div>

    <!-- Manage preferences sub-modal: two toggles (Analytics / Marketing),
         both default unchecked; Required is an always-on pill, not a toggle. -->
    <b-modal
      v-model="manageOpen"
      title="Manage cookie preferences"
      ok-title="Save preferences"
      ok-variant="primary"
      cancel-variant="outline-secondary"
      @ok="saveGranular"
    >
      <div class="d-flex align-items-center justify-content-between mb-3">
        <div>
          <strong>Required</strong>
          <div class="text-muted small">
            Needed for the site to function (session, security).
          </div>
        </div>
        <b-badge variant="secondary" pill>Always Active</b-badge>
      </div>
      <b-form-checkbox
        v-model="granular.analytics"
        switch
        class="mb-3"
        v-b-tooltip.hover
        title="Allow Invown's own usage analytics. Turning this off blocks Invown analytics cookies but keeps the site working."
      >
        <strong>Analytics</strong>
        <div class="text-muted small">
          Helps us understand how the site is used.
        </div>
      </b-form-checkbox>
      <b-form-checkbox
        v-model="granular.marketing"
        switch
        class="mb-3"
        v-b-tooltip.hover
        title="Allow issuer marketing pixels to use cookies so issuers can attribute their ad campaigns to this listing. Off by default."
      >
        <strong>Marketing</strong>
        <div class="text-muted small">
          Lets issuers attribute their ad campaigns to this listing.
        </div>
      </b-form-checkbox>
      <b-link v-if="homeId" href="#" @click.prevent="openHomeDisclosure">
        See which marketing providers are active on this offering
      </b-link>
      <b-link
        v-else
        href="/api/help/articles/marketing-providers-invown-may-share-with"
        target="_blank"
      >See the marketing providers Invown may share with</b-link>
    </b-modal>
  </div>
</template>

<script>
import backend from "@/services/backend";

const CONSENT_COOKIE = "inv_consent_marketing";

export default {
  name: "ConsentBanner",
  props: {
    // When on a listing page, the route's home_id deep-links the Manage modal
    // to the per-home disclosure modal (§5a); otherwise the generic article.
    homeId: { type: Number, default: 0 },
  },
  data() {
    return {
      decided: this.hasConsentCookie(),
      manageOpen: false,
      granular: { analytics: false, marketing: false },
    };
  },
  computed: {
    showBanner() {
      return !this.decided;
    },
  },
  mounted() {
    window.addEventListener("invown:open-cookie-settings", this.reopen);
  },
  beforeDestroy() {
    window.removeEventListener("invown:open-cookie-settings", this.reopen);
  },
  methods: {
    hasConsentCookie() {
      return ("; " + document.cookie).indexOf("; " + CONSENT_COOKIE + "=") !== -1;
    },
    reopen() {
      // Footer "Cookie settings" link re-opens the chooser from any page.
      this.decided = false;
    },
    openManage() {
      this.manageOpen = true;
    },
    openHomeDisclosure() {
      this.manageOpen = false;
      window.dispatchEvent(new CustomEvent("invown:open-home-disclosure"));
    },
    async choose(decision) {
      try {
        await backend.setMarketingConsent({ decision });
      } catch (e) {
        // Banner still records locally even if the network call fails.
        document.cookie =
          CONSENT_COOKIE + "=" + decision + ";path=/;max-age=" + 60 * 60 * 24 * 365;
      }
      this.decided = true;
    },
    async saveGranular() {
      const payload = {
        decision: "granular",
        granular: {
          analytics: !!this.granular.analytics,
          marketing: !!this.granular.marketing,
        },
      };
      try {
        await backend.setMarketingConsent(payload);
      } catch (e) {
        document.cookie =
          CONSENT_COOKIE +
          "=granular:" +
          JSON.stringify(payload.granular) +
          ";path=/;max-age=" +
          60 * 60 * 24 * 365;
      }
      this.decided = true;
    },
  },
};
</script>

<style scoped>
.consent-banner {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: var(--z-consent, 1045); /* DS-6: above sticky bars + sidebars, below modals */
  background: #fff;
  border-top: 1px solid #d4ddfc;
  box-shadow: 0 -2px 12px rgba(34, 60, 155, 0.08);
  padding: 14px 0;
}
.consent-banner__copy {
  font-size: 14px;
}
.consent-banner__actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
}
/* Equal prominence: identical width on the two primary choices (cond #3). */
.consent-banner__btn {
  min-width: 120px;
}
</style>
